import { useMemo } from 'react';
import { upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import { Pill } from '../../common/Pill';
import { ApprovedStatusLabel, approvedStatusOptions } from '../../../constants/approvedStatus';
import styles from './PricingFiltersSummary.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  PRICING_FILTER_CHANNELS_KEY,
  PRICING_FILTER_CURRENCIES_KEY,
  PRICING_FILTER_HAS_PRICE_KEY,
  PRICING_FILTER_STATUSES_KEY,
  setPricingFilter,
} from '../../../store/actions/pricing';
import { isEmptyFilter } from '../../../utils/pricings';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';
import { getSupplierRetailersFromStateSelector } from '../../../utils/stateHelper';

export function PricingFiltersSummary(props) {
  const { currencies, isChannelsFilterAvailable, channels } = props;
  const { filters } = useSelector((state) => state.pricings);
  const dispatch = useDispatch();
  const supplierRetailers = useSelector(getSupplierRetailersFromStateSelector);

  const visibleFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          if (key === PRICING_FILTER_CHANNELS_KEY && !isChannelsFilterAvailable) return false;

          return !isEmptyFilter({
            key,
            value,
            pricingCurrencies: currencies,
            pricingChannels: channels,
          });
        }),
      ),
    [filters, currencies, channels, isChannelsFilterAvailable],
  );

  const hasFilters = useMemo(() => Object.keys(visibleFilters).length > 0, [visibleFilters]);

  const displayValue = (key, value) => {
    switch (key) {
      case PRICING_FILTER_STATUSES_KEY:
        return value.map((item) => ApprovedStatusLabel[item]).join(', ');
      case PRICING_FILTER_CURRENCIES_KEY:
        return value.join(', ');
      case PRICING_FILTER_CHANNELS_KEY:
        const relevantChannels = channels?.filter((channel) => value?.includes(channel?.id)) || [];
        return relevantChannels
          .map((channel) =>
            supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({ supplierRetailers, retailerId: channel.id }),
          )
          .join(', ');
      case PRICING_FILTER_HAS_PRICE_KEY:
        return '';
      default:
        return value;
    }
  };

  const displayKey = (key) => {
    switch (key) {
      case PRICING_FILTER_STATUSES_KEY:
        return 'Status';
      case PRICING_FILTER_CURRENCIES_KEY:
        return 'Currency';
      case PRICING_FILTER_HAS_PRICE_KEY:
        return 'Has Price';
      default:
        break;
    }
    return upperFirst(key);
  };

  const clearFilter = (key) => {
    let value = '';
    switch (key) {
      case PRICING_FILTER_STATUSES_KEY:
        value = approvedStatusOptions.map((status) => status.value);
        break;
      case PRICING_FILTER_CURRENCIES_KEY:
        value = currencies;
        break;
      case PRICING_FILTER_CHANNELS_KEY:
        value = channels.map((channel) => channel.id);
        break;
      case PRICING_FILTER_HAS_PRICE_KEY:
        value = false;
        break;
      default:
        break;
    }
    dispatch(setPricingFilter({ [key]: value }));
  };

  return (
    <div className={styles.filtersSummary}>
      {hasFilters && (
        <>
          <span className={styles.filtersSummaryTitle}>Current Filters:</span>

          {Object.entries(visibleFilters).map(([key, value]) => (
            <Pill
              key={key}
              title={displayKey(key)}
              value={displayValue(key, value)}
              onRemove={() => clearFilter(key)}
            />
          ))}
        </>
      )}
    </div>
  );
}

PricingFiltersSummary.propTypes = {
  currencies: PropTypes.arrayOf(PropTypes.string),
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
};
